/** Display combined score as a whole number. */
export function formatTotalScore(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return String(Math.round(n));
}

export function getScoreWeights(judgesPercent, usersPercent) {
  const jp = Number(judgesPercent);
  const up = Number(usersPercent);
  if (!Number.isFinite(jp) || !Number.isFinite(up)) return null;
  return {
    judgeMultiplier: jp / 100,
    voteMultiplier: up / 100,
    judgesPercent: jp,
    usersPercent: up,
  };
}

export function formatMultiplier(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  if (Number.isInteger(n)) return String(n);
  const rounded = Math.round(n * 100) / 100;
  return String(rounded);
}

function formatPart(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}

export function computeScoreParts(row, weights) {
  if (!weights) return null;
  const judgeAvg = Number(row?.judge_avg) || 0;
  const publicVotes =
    Number(row?.public_votes ?? row?.voteCount ?? row?.vote_count) || 0;
  const adj = Number(row?.admin_adjustment) || 0;
  const judgePart = judgeAvg * weights.judgeMultiplier;
  const votePart = publicVotes * weights.voteMultiplier;
  const total = judgePart + votePart + adj;
  return {
    judgeAvg,
    publicVotes,
    adj,
    judgePart,
    votePart,
    total,
  };
}

/** e.g. "(6×3) + (0×0.7) = 18" */
export function formatScoreEquation(row, weights) {
  const parts = computeScoreParts(row, weights);
  if (!parts || parts.judgeAvg <= 0) return null;

  const jm = formatMultiplier(weights.judgeMultiplier);
  const vm = formatMultiplier(weights.voteMultiplier);
  const judgeTerm = `${formatPart(parts.judgeAvg)}×${jm}`;
  const voteTerm = `${parts.publicVotes}×${vm}`;
  const roundedTotal = Math.round(parts.total * 10) / 10;

  let equation = `(${judgeTerm}) + (${voteTerm})`;
  if (parts.adj !== 0) {
    const sign = parts.adj > 0 ? "+" : "−";
    equation += ` ${sign} ${Math.abs(parts.adj)}`;
  }
  equation += ` = ${roundedTotal}`;
  return equation;
}

export function formatScoreFormulaSummary(judgesPercent, usersPercent) {
  const weights = getScoreWeights(judgesPercent, usersPercent);
  if (!weights) return null;

  const jm = formatMultiplier(weights.judgeMultiplier);
  const vm = formatMultiplier(weights.voteMultiplier);

  return {
    judgeLine: `Judge score × ${weights.judgesPercent}% = × ${jm} per point`,
    voteLine: `Public votes × ${weights.usersPercent}% = × ${vm} per vote`,
    exampleLine: `Example: 7/10 judge, 2 votes → (7×${jm}) + (2×${vm}) = ${(
      7 * weights.judgeMultiplier +
      2 * weights.voteMultiplier
    ).toFixed(1)}`,
  };
}

export function formatRankingGuide(judgesPercent, usersPercent) {
  const summary = formatScoreFormulaSummary(judgesPercent, usersPercent);
  if (!summary) {
    return "Rankings use combined judge scores and public votes — not votes alone.";
  }

  return (
    `Combined score = judge points + vote points. ` +
    `${summary.judgeLine}. ` +
    `${summary.voteLine}. ` +
    `${summary.exampleLine}. ` +
    `A higher judge score can rank above someone with more votes.`
  );
}

/** Gold-themed scrollbar styles for dark panels. */
export const goldScrollbarSx = {
  scrollbarWidth: "thin",
  scrollbarColor: "rgba(201,166,107,0.5) rgba(255,255,255,0.04)",
  "&::-webkit-scrollbar": { width: 8 },
  "&::-webkit-scrollbar-track": {
    bgcolor: "rgba(255,255,255,0.04)",
    borderRadius: 4,
  },
  "&::-webkit-scrollbar-thumb": {
    bgcolor: "rgba(201,166,107,0.4)",
    borderRadius: 4,
    border: "2px solid transparent",
    backgroundClip: "padding-box",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    bgcolor: "rgba(201,166,107,0.65)",
  },
};
